/* ============================================================
   Wido Ops — Employee dashboard. Reads tweaks:
     t.dashLayout : 'balanced' | 'charts' | 'cards'
     t.fundViz    : 'donut' | 'linear' | 'segmented'
   Exports window.Dashboard({ t, me, month, onNav }).
   ============================================================ */
(function () {
  const D = window.WidoData;
  const { DonutGauge, LinearMeter, SegmentMeter, BarChart, StackBars, Sparkline } = window;

  function buildBars(records) {
    return records.map((r) => ({
      label: String(r.day).padStart(2, "0"),
      v: r.weekend ? 0 : (r.lateMin || 0),
      weekend: !!r.weekend,
      today: r.day === D.DAYS_ELAPSED,
    }));
  }
  function buildStack(records, offsetMin) {
    // An approved leave-offset turns some unpaid (work) minutes into annual leave.
    // Distribute it greedily, earliest unpaid day first (mirrors the backend
    // month_metrics pass), so each day's bar shows an "offset" slice carved out of
    // its unpaid portion.
    let offLeft = offsetMin || 0;
    return records.filter((r) => !r.weekend && (r.lateMin + r.earlyMin) > 0).map((r) => {
      const total = r.lateMin + r.earlyMin;
      const daily = r.dailyHit || 0;
      const free = (r.freeHit != null) ? r.freeHit : (total - daily - (r.fundHit || 0) - (r.workHit || 0));
      let work = r.workHit || 0;
      let offset = 0;
      if (offLeft > 0 && work > 0) { offset = Math.min(work, offLeft); work -= offset; offLeft -= offset; }
      return { label: "Jun " + String(r.day).padStart(2, "0"), daily, free: Math.max(0, free), fund: r.fundHit || 0, offset, work };
    });
  }

  function StatCard({ icon, iconBg, iconColor, value, unit, label, foot, footColor }) {
    return React.createElement("div", { className: "card stat" },
      React.createElement("div", { className: "stat__top" },
        React.createElement("div", { className: "stat__val" }, value, unit && React.createElement("span", { className: "stat__unit" }, " " + unit)),
        React.createElement("div", { className: "stat__icon", style: { background: iconBg } },
          React.createElement("span", { className: "material-symbols-rounded", style: { color: iconColor } }, icon)),
      ),
      React.createElement("div", { className: "stat__label" }, label),
      foot && React.createElement("div", { className: "stat__foot", style: { color: footColor || "var(--md-on-surface-variant)" } }, foot),
    );
  }

  function ClockCard({ me, month, readOnly }) {
    // latest day with an actual punch; fall back to the latest working day up to
    // today (never a future day — matters early in a fresh month before the door
    // reader has synced any punch, when there are no punches to anchor on).
    const days = (month.records || []).filter((r) => !r.weekend);
    const withPunch = days.filter((r) => r.in && r.in !== "—");
    // The dashboard's month (getMonthFor) carries no year/month and is always the
    // live period, so treat "unlabelled" as current; an explicit past month isn't.
    const isCurMonth = (month.month == null) || (month.year === D.CUR_YEAR && month.month === D.CUR_MONTH);
    const elapsed = isCurMonth ? (D.DAYS_ELAPSED || 0) : Infinity;
    const upToToday = days.filter((r) => !r.day || r.day <= elapsed);
    const today = withPunch.length ? withPunch[withPunch.length - 1] : (upToToday.length ? upToToday[upToToday.length - 1] : null);
    const punched = !!(today && today.in && today.in !== "—");
    const lateToday = today && today.lateMin ? today.lateMin : 0;
    const inTime = punched ? today.in : "—:—";
    const dayLabel = today && today.date ? D.helpers.fmtDate(today.date) : (today ? "Latest entry" : "No entries yet");
    return React.createElement("div", { className: "clock" },
      React.createElement("div", { style: { flex: 1 } },
        React.createElement("div", { className: "eyebrow", style: { color: "rgba(255,255,255,.6)" } }, readOnly ? (me.name + " · " + dayLabel) : dayLabel),
        React.createElement("div", { className: "clock__time" }, inTime),
        React.createElement("div", { style: { font: "400 14px/1.4 var(--font-sans)", color: "rgba(255,255,255,.7)", marginTop: 4 } },
          !punched ? "Attendance syncs from the door reader" : (lateToday ? `Clocked in ${lateToday}m late` : "On time")),
      ),
      readOnly
        ? React.createElement("span", { className: "tag", style: { background: "rgba(255,255,255,.16)", color: "#fff" } }, React.createElement("span", { className: "material-symbols-rounded" }, "visibility"), "Manager view")
        : React.createElement("div", { className: "stack-v", style: { gap: 8, alignItems: "flex-end" } },
            // Attendance is captured by the door reader (device → agent → backend),
            // so there's no manual clock action — show the sync status instead.
            React.createElement("span", { className: "tag", style: { background: "rgba(255,255,255,.16)", color: "#fff" } },
              React.createElement("span", { className: "material-symbols-rounded" }, "sensors"), "Auto-tracked"),
            React.createElement("span", { style: { font: "400 12px/1 var(--font-sans)", color: "rgba(255,255,255,.55)" } }, "Synced from the door reader")),
    );
  }

  function FundCard({ month, viz, onNav }) {
    const used = month.fundUsed, remaining = month.fundRemaining, total = month.fundTotal || D.FUND_TOTAL;
    const pctUsed = Math.round((used / total) * 100);
    const danger = remaining <= 60;
    let vizEl;
    if (viz === "linear") {
      vizEl = React.createElement("div", { className: "stack-v", style: { gap: 14, width: "100%" } },
        React.createElement("div", { className: "between" },
          React.createElement("span", { className: "mono", style: { font: "600 36px/1 var(--font-mono)", color: danger ? "var(--md-error)" : "var(--md-on-surface)" } }, remaining),
          React.createElement("span", { className: "muted mono" }, "/ " + total + " min left"),
        ),
        React.createElement(LinearMeter, { value: used, total, height: 16, danger: total - 60, color: "var(--md-primary)" }),
        React.createElement("div", { className: "between muted", style: { fontSize: 13 } },
          React.createElement("span", null, used + "m used"),
          React.createElement("span", null, pctUsed + "% of monthly fund"),
        ),
      );
    } else if (viz === "segmented") {
      vizEl = React.createElement("div", { className: "stack-v", style: { gap: 16, width: "100%" } },
        React.createElement("div", { className: "row", style: { alignItems: "baseline", gap: 8 } },
          React.createElement("span", { className: "mono", style: { font: "600 36px/1 var(--font-mono)", color: danger ? "var(--md-error)" : "var(--md-on-surface)" } }, remaining),
          React.createElement("span", { className: "muted mono" }, "/ " + total + " min left"),
        ),
        React.createElement(SegmentMeter, { value: remaining, total, segments: 30, color: danger ? "var(--md-error)" : "var(--md-primary)" }),
        React.createElement("div", { className: "muted", style: { fontSize: 13 } }, "Each segment ≈ 10 minutes"),
      );
    } else {
      vizEl = React.createElement(DonutGauge, {
        value: remaining, total, size: 184, stroke: 18,
        color: danger ? "var(--md-error)" : "var(--md-primary)",
        label: String(remaining), sublabel: "min left of " + total,
      });
    }
    return React.createElement("div", { className: "card card--pad", style: { display: "flex", flexDirection: "column", gap: 20 } },
      React.createElement("div", { className: "between" },
        React.createElement("div", null,
          React.createElement("div", { className: "eyebrow" }, window.t("Flexible fund") + " · " + D.MONTH_LABEL),
          React.createElement("h3", { className: "section-title", style: { fontSize: 20, marginTop: 4 } }, total + (window.WidoI18n.lang === "vi" ? " " + window.t("minute allowance") : "-minute allowance"),
            (month.fundTotal && month.fundTotal !== D.FUND_TOTAL) ? React.createElement("span", { className: "tag tag--info", style: { marginLeft: 8, verticalAlign: "middle", fontSize: 10 } }, React.createElement("span", { className: "material-symbols-rounded" }, "tune"), "Custom") : null),
        ),
        danger && React.createElement("span", { className: "tag tag--err" }, React.createElement("span", { className: "material-symbols-rounded" }, "warning"), window.t("Running low")),
      ),
      React.createElement("div", { style: { display: "grid", placeItems: "center", padding: viz === "donut" ? "8px 0" : 0 } }, vizEl),
      React.createElement("div", { className: "divider" }),
      React.createElement("div", { className: "between" },
        React.createElement("div", { className: "muted", style: { fontSize: 13, lineHeight: 1.4 } },
          "After the fund is spent, late/early minutes are deducted from work hours."),
        React.createElement("button", { className: "btn btn--text btn--sm", onClick: () => onNav("policies") }, window.t("Rules")),
      ),
    );
  }

  function LateChartCard({ month }) {
    const bars = buildBars(month.records);
    const lateTotal = month.records.reduce((s, r) => s + (r.lateMin || 0), 0);
    const lateDays = month.records.filter((r) => r.lateMin > 0).length;
    return React.createElement("div", { className: "card card--pad", style: { display: "flex", flexDirection: "column", gap: 18 } },
      React.createElement("div", { className: "between wrap" },
        React.createElement("div", null,
          React.createElement("div", { className: "eyebrow" }, window.t("Late minutes per day")),
          React.createElement("h3", { className: "section-title", style: { fontSize: 20, marginTop: 4 } },
            React.createElement("span", { className: "mono" }, lateTotal), " min ",
            React.createElement("span", { className: "muted", style: { fontSize: 15, fontWeight: 400 } }, "across " + lateDays + " days")),
        ),
        React.createElement("div", { className: "legend" },
          React.createElement("span", { className: "legend__item" }, React.createElement("span", { className: "legend__dot", style: { background: "var(--md-primary)" } }), "≤ 60m"),
          React.createElement("span", { className: "legend__item" }, React.createElement("span", { className: "legend__dot", style: { background: "var(--md-tertiary)" } }), "> 60m"),
        ),
      ),
      React.createElement(BarChart, { data: bars, height: 190, danger: 60 }),
    );
  }

  function BreakdownCard({ month, settings, person }) {
    // An approved leave-offset converts some unpaid minutes to annual leave, so the
    // amount STILL falling on pay is the raw overflow minus what was offset.
    const rawWork = month.workMinDeducted || 0;
    const offsetMin = Math.min(month.offsetMin || 0, rawWork);
    const totalWork = Math.max(0, rawWork - offsetMin);
    const dailyTotal = month.dailyUsed || month.records.reduce((s, r) => s + (r.dailyHit || 0), 0);
    // Active daily grant(s) for this person/month — surface the full reason so
    // it's clear *why* those minutes were covered before the fund.
    const grants = (settings && person) ? D.helpers.dailyGrantsFor(person.id, settings, month.year || 2026, month.month || 6) : [];
    const dailyReason = grants.map((g) => g.reason).filter(Boolean).join(" · ");
    const rows = buildStack(month.records, offsetMin).map((r) => r.daily ? Object.assign({}, r, { dailyReason }) : r);
    return React.createElement("div", { className: "card card--pad", style: { display: "flex", flexDirection: "column", gap: 18 } },
      React.createElement("div", null,
        React.createElement("div", { className: "eyebrow" }, window.t("How each absence was covered")),
        React.createElement("h3", { className: "section-title", style: { fontSize: 20, marginTop: 4 } }, window.t("Deduction breakdown")),
      ),
      month.hasDaily && React.createElement("div", { className: "banner", style: { background: "var(--md-tertiary-container)", color: "var(--md-on-tertiary-container)" } },
        React.createElement("span", { className: "material-symbols-rounded" }, "child_care"),
        React.createElement("span", { style: { fontSize: 13, lineHeight: 1.5 } },
          React.createElement("strong", null, window.t("Daily free minutes active")),
          dailyReason ? React.createElement(React.Fragment, null, " · ", React.createElement("em", { style: { fontStyle: "normal", opacity: .85 } }, dailyReason)) : null,
          " — " + window.t("covered") + " ",
          React.createElement("span", { className: "mono" }, dailyTotal + "m"), " " + window.t("this month before touching your fund.")),
      ),
      rows.length ? React.createElement(StackBars, { rows }) :
        React.createElement("div", { className: "empty" }, React.createElement("span", { className: "material-symbols-rounded" }, "check_circle"), React.createElement("p", null, window.t("No deductions yet this month."))),
      React.createElement("div", { className: "divider" }),
      React.createElement("div", { className: "legend" },
        (month.hasDaily || dailyTotal > 0) && React.createElement("span", { className: "legend__item" }, React.createElement("span", { className: "legend__dot", style: { background: "var(--ded-daily)" } }), window.t("Daily grant")),
        React.createElement("span", { className: "legend__item" }, React.createElement("span", { className: "legend__dot", style: { background: "var(--ded-free)" } }), window.t("Free (≤60m permission)")),
        React.createElement("span", { className: "legend__item" }, React.createElement("span", { className: "legend__dot", style: { background: "var(--ded-fund)" } }), window.t("From fund")),
        offsetMin > 0 && React.createElement("span", { className: "legend__item" }, React.createElement("span", { className: "legend__dot", style: { background: "var(--ded-offset)" } }), window.t("Offset to leave")),
        React.createElement("span", { className: "legend__item" }, React.createElement("span", { className: "legend__dot", style: { background: "var(--ded-unpaid)" } }), window.t("Unpaid hours")),
      ),
      offsetMin > 0 && React.createElement("div", { className: "banner", style: { background: "var(--md-primary-container)", color: "var(--md-on-primary-container)" } },
        React.createElement("span", { className: "material-symbols-rounded" }, "currency_exchange"),
        React.createElement("span", { style: { fontSize: 13, lineHeight: 1.5 } },
          React.createElement("strong", null, offsetMin + " min"), " " + window.t("offset to annual leave") + " (",
          React.createElement("span", { className: "mono" }, (offsetMin / 480).toFixed(2)), " " + window.t("days") + ") — " + window.t("protected from pay deduction.")),
      ),
      totalWork > 0 && React.createElement("div", { className: "banner" },
        React.createElement("span", { className: "material-symbols-rounded" }, "info"),
        React.createElement("span", { style: { fontSize: 13, lineHeight: 1.5 } },
          React.createElement("strong", null, totalWork + " min"), " currently fall on work hours (≈ ",
          React.createElement("span", { className: "mono" }, (totalWork / 60).toFixed(2)), " h). You can offset these with annual leave before payroll close."),
      ),
    );
  }

  function MiniStats({ month }) {
    const lateTotal = month.records.reduce((s, r) => s + (r.lateMin || 0), 0);
    const earlyTotal = month.records.reduce((s, r) => s + (r.earlyMin || 0), 0);
    const earlyDays = month.records.filter((r) => r.earlyMin > 0).length;
    return React.createElement("div", { className: "statgrid statgrid--5" },
      React.createElement(StatCard, {
        icon: "savings", iconBg: "var(--md-primary-container)", iconColor: "var(--md-on-primary-container)",
        value: month.fundRemaining, unit: window.t("min"), label: window.t("Flexible fund remaining"),
        foot: React.createElement(React.Fragment, null, React.createElement("span", { className: "material-symbols-rounded", style: { fontSize: 16 } }, "trending_down"), month.fundUsed + " of " + (month.fundTotal || D.FUND_TOTAL) + " used"),
      }),
      React.createElement(StatCard, {
        icon: "event_available", iconBg: "var(--md-tertiary-container)", iconColor: "var(--md-on-tertiary-container)",
        value: month.permRemaining, unit: "/ " + (month.permTotal || D.PERMISSIONS_TOTAL), label: window.t("Permission requests left"),
        foot: month.permRemaining <= 1 ? window.t("Use the last one wisely") : window.t("Resets next month"),
      }),
      React.createElement(StatCard, {
        icon: "schedule", iconBg: "var(--md-secondary-container)", iconColor: "var(--md-on-secondary-container)",
        value: lateTotal, unit: window.t("min"), label: window.t("Late this month"),
        foot: month.records.filter((r) => r.lateMin > 0).length + " " + window.t("late days"),
      }),
      React.createElement(StatCard, {
        icon: "logout", iconBg: "var(--md-tertiary-container)", iconColor: "var(--md-on-tertiary-container)",
        value: earlyTotal, unit: window.t("min"), label: window.t("Early leave this month"),
        foot: earlyDays + " " + window.t("early days"),
      }),
      React.createElement(StatCard, {
        icon: "beach_access", iconBg: "var(--md-surface-container-high)", iconColor: "var(--md-primary)",
        value: month.leave ? round2(month.leave.available) : Math.max(0, 6 - month.leaveDays), unit: window.t("days"), label: window.t("Annual leave available"),
        foot: month.leave
          ? React.createElement(React.Fragment, null,
              React.createElement("span", { className: "material-symbols-rounded", style: { fontSize: 16 } }, "event_available"),
              round2(month.leave.accrued) + " accrued of " + month.leave.entitlement + "/yr · " + round2(month.leave.taken) + " taken",
              month.leave.custom ? React.createElement("span", { className: "tag tag--info", style: { marginLeft: 6, fontSize: 10 } }, "Custom") : null)
          : "Accrues 1 day per month",
      }),
    );
  }

  // min -> "7.8h" (1 decimal). days -> "1.5" (trim). minutes -> "140h30m".
  function fmtH(min) { return (Math.round((min / 60) * 10) / 10).toFixed(1) + "h"; }
  function fmtDays(d) { return (Math.round(d * 10) / 10).toString(); }
  // Round a day count to at most 2 decimals for display (e.g. 10.8021 -> 10.8, 10.875 -> 10.88).
  function round2(n) { return Math.round((n || 0) * 100) / 100; }
  function fmtHM(min) {
    min = Math.max(0, Math.round(min));
    const h = Math.floor(min / 60), m = min % 60;
    if (!h) return m + "m";
    return m ? h + "h" + String(m).padStart(2, "0") + "m" : h + "h";
  }

  // Paid-time "Hours summary" — standard / actual / OT / paid-leave / unpaid /
  // paid / lunch. All derived from the month via D.helpers.monthMetrics (8h/day).
  function PaidSummary({ month, settings }) {
    const m = D.helpers.monthMetrics(month, settings);
    const items = [
      { icon: "calendar_month", c: "var(--md-on-surface-variant)", label: window.t("Standard hours"), val: fmtH(m.standardMin), sub: "(" + m.standardDays + " " + window.t("days") + ")" },
      { icon: "work_history", c: "#2a6fdb", label: window.t("Actual work hours"), val: fmtHM(m.actualWorkMin), sub: "(" + fmtDays(m.actualWorkMin / 480) + " " + window.t("days") + ")" },
      { icon: "more_time", c: "#7b4fd6", label: window.t("Overtime hours"), val: fmtH(m.overtimeMin) },
      { icon: "beach_access", c: "#1F8A5B", label: window.t("Paid leave hours"), val: fmtH(m.paidLeaveMin),
        sub: m.paidLeaveMin ? ("(" + fmtDays(m.leaveDays) + " " + window.t("days") + (m.offsetMin ? " · " + window.t("incl.") + " " + fmtH(m.offsetMin) + " " + window.t("offset") : "") + ")") : null },
      { icon: "money_off", c: "var(--md-error)", label: window.t("Unpaid hours"), val: fmtH(m.unpaidMin) },
      { icon: "payments", c: "var(--md-primary)", label: window.t("Paid hours"), val: fmtH(m.paidMin), sub: "(" + fmtDays(m.paidDays) + " " + window.t("days") + ")", strong: true },
      { icon: "restaurant", c: "#D6841E", label: window.t("Lunch days"), val: fmtDays(m.lunchDays), sub: window.t("days") },
    ];
    return React.createElement("div", { className: "card card--pad", style: { display: "flex", flexDirection: "column", gap: 16 } },
      React.createElement("div", null,
        React.createElement("div", { className: "eyebrow" }, window.t("Paid time") + " · " + D.MONTH_LABEL),
        React.createElement("h3", { className: "section-title", style: { fontSize: 20, marginTop: 4 } }, window.t("Hours summary"))),
      React.createElement("div", { className: "metricgrid" },
        items.map((it, i) => React.createElement("div", { key: i, className: "metric" + (it.strong ? " metric--strong" : "") },
          React.createElement("div", { className: "metric__top" },
            React.createElement("span", { className: "material-symbols-rounded", style: { color: it.c, fontSize: 20 } }, it.icon),
            React.createElement("span", { className: "metric__label" }, it.label)),
          React.createElement("div", { className: "row", style: { alignItems: "baseline", gap: 6, flexWrap: "wrap" } },
            React.createElement("span", { className: "metric__val mono", style: it.strong ? { color: "var(--md-primary)" } : null }, it.val),
            it.sub && React.createElement("span", { className: "metric__sub mono" }, it.sub))))),
    );
  }

  function Dashboard({ t, me, month, onNav, readOnly, settings }) {
    const layout = t.dashLayout || "balanced";
    const viz = t.fundViz || "donut";

    const fund = React.createElement(FundCard, { key: "fund", month, viz, onNav });
    const stats = React.createElement(MiniStats, { key: "stats", month });
    const paid = React.createElement(PaidSummary, { key: "paid", month, settings });
    const lateChart = React.createElement(LateChartCard, { key: "late", month });
    const breakdown = React.createElement(BreakdownCard, { key: "break", month, settings, person: me });
    const clock = React.createElement(ClockCard, { key: "clock", me, month, readOnly });

    let body;
    if (layout === "charts") {
      body = React.createElement(React.Fragment, null,
        clock,
        lateChart,
        React.createElement("div", { className: "grid-2" }, fund, breakdown),
        paid,
        stats,
      );
    } else if (layout === "cards") {
      body = React.createElement(React.Fragment, null,
        clock,
        stats,
        paid,
        React.createElement("div", { className: "grid-2" }, fund, breakdown),
        lateChart,
      );
    } else { // balanced
      body = React.createElement(React.Fragment, null,
        React.createElement("div", { className: "dash-hero" }, clock, fund),
        stats,
        paid,
        React.createElement("div", { className: "grid-2" }, lateChart, breakdown),
      );
    }
    return React.createElement("div", { className: "stack-v", style: { gap: 24 } }, body);
  }

  window.Dashboard = Dashboard;
})();
